/* ══════════════════════════════════════════════════════════════
   i18n — Lightweight Internationalization System
   No external dependencies. Supports 8 Indian languages.
   ══════════════════════════════════════════════════════════════ */

export type Locale = 'en' | 'hi' | 'ta' | 'te' | 'mr' | 'bn' | 'gu' | 'kn';

export interface LocaleInfo {
    code: Locale;
    name: string;
    nativeName: string;
    script: string;
    flag: string;
}

export const SUPPORTED_LOCALES: LocaleInfo[] = [
    { code: 'en', name: 'English', nativeName: 'English', script: 'Latin', flag: '🇬🇧' },
    { code: 'hi', name: 'Hindi', nativeName: 'हिन्दी', script: 'Devanagari', flag: '🇮🇳' },
    { code: 'ta', name: 'Tamil', nativeName: 'தமிழ்', script: 'Tamil', flag: '🇮🇳' },
    { code: 'te', name: 'Telugu', nativeName: 'తెలుగు', script: 'Telugu', flag: '🇮🇳' },
    { code: 'mr', name: 'Marathi', nativeName: 'मराठी', script: 'Devanagari', flag: '🇮🇳' },
    { code: 'bn', name: 'Bengali', nativeName: 'বাংলা', script: 'Bengali', flag: '🇮🇳' },
    { code: 'gu', name: 'Gujarati', nativeName: 'ગુજરાતી', script: 'Gujarati', flag: '🇮🇳' },
    { code: 'kn', name: 'Kannada', nativeName: 'ಕನ್ನಡ', script: 'Kannada', flag: '🇮🇳' },
];

type TranslationValue = string | { [key: string]: TranslationValue };
type TranslationMap = Record<string, TranslationValue>;
type FlatTranslationMap = Record<string, string>;

const translations: Record<Locale, FlatTranslationMap> = {
    en: {}, hi: {}, ta: {}, te: {}, mr: {}, bn: {}, gu: {}, kn: {},
};

let currentLocale: Locale = 'en';
const changeListeners: Array<(locale: Locale) => void> = [];

/** Flatten nested translation objects into dot-notation keys */
function flatten(obj: TranslationMap, prefix = ''): FlatTranslationMap {
    const result: FlatTranslationMap = {};
    for (const key of Object.keys(obj)) {
        const fullKey = prefix ? `${prefix}.${key}` : key;
        const value = obj[key];
        if (typeof value === 'string') {
            result[fullKey] = value;
        } else if (typeof value === 'object' && value !== null) {
            Object.assign(result, flatten(value as TranslationMap, fullKey));
        }
    }
    return result;
}

/** Register a locale's translations */
export function registerLocale(locale: Locale, data: TranslationMap): void {
    translations[locale] = flatten(data);
}

/** Detect browser language and map to supported locale */
function detectLocale(): Locale {
    const stored = localStorage.getItem('imm-locale') as Locale | null;
    if (stored && translations[stored]) return stored;

    const browserLang = navigator.language?.toLowerCase() || '';
    if (browserLang.startsWith('hi')) return 'hi';
    if (browserLang.startsWith('ta')) return 'ta';
    if (browserLang.startsWith('te')) return 'te';
    if (browserLang.startsWith('mr')) return 'mr';
    if (browserLang.startsWith('bn')) return 'bn';
    if (browserLang.startsWith('gu')) return 'gu';
    if (browserLang.startsWith('kn')) return 'kn';
    return 'en';
}

/** Get current locale */
export function getLocale(): Locale {
    return currentLocale;
}

/** Set locale and notify listeners */
export function setLocale(locale: Locale): void {
    if (!SUPPORTED_LOCALES.find(l => l.code === locale)) return;
    currentLocale = locale;
    localStorage.setItem('imm-locale', locale);
    document.documentElement.setAttribute('lang', locale);
    changeListeners.forEach(cb => cb(locale));
}

/** Subscribe to locale changes. Returns unsubscribe function. */
export function onLocaleChange(callback: (locale: Locale) => void): () => void {
    changeListeners.push(callback);
    return () => {
        const idx = changeListeners.indexOf(callback);
        if (idx >= 0) changeListeners.splice(idx, 1);
    };
}

/** Translate a key. Falls back to English, then to the key itself.
 *  Supports interpolation: t('hello', { name: 'Aditya' }) → "Hello, Aditya"
 */
export function t(key: string, params?: Record<string, string | number>): string {
    let text = translations[currentLocale]?.[key]
        || translations.en?.[key]
        || key;

    if (params) {
        for (const [k, v] of Object.entries(params)) {
            text = text.replace(new RegExp(`\\{\\{${k}\\}\\}`, 'g'), String(v));
        }
    }
    return text;
}

/** Initialize the i18n system — call once at app boot */
export function initI18n(): void {
    currentLocale = detectLocale();
    document.documentElement.setAttribute('lang', currentLocale);
}
